import { useState, useEffect } from 'react';
import { Shield, AlertCircle, Search } from 'lucide-react';
import Layout from '../components/Layout';
import { adminApi } from '../lib/api';
import type { AdminUser } from '../lib/api';
import { Card, PageHeader, Badge, Spinner } from '../components/ui';

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      if (response.success && response.data) {
        const data = response.data as unknown as { users: AdminUser[] };
        setUsers(data.users || []);
        setError(null);
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId: string) => {
    try {
      const response = await adminApi.toggleAdmin(userId);
      if (response.success) {
        await loadUsers(); // Reload the user list
        setError(null);
      } else {
        setError(response.message || 'Failed to update user role');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin"
        title="User Management"
        description="Manage user roles and permissions."
        actions={
          <div className="relative">
            <Search className="w-5 h-5 text-ink-3 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ds-input !pl-10"
            />
          </div>
        }
      />

      <Card>
        {error && (
          <div className="p-4 bg-danger-soft border-b border-line flex items-center">
            <AlertCircle className="h-5 w-5 text-danger" />
            <p className="ml-3 text-sm text-danger">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-2">
                <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Created</th>
                <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                  <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                    {user.firstName} {user.lastName}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap font-mono">
                    {user.email}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-ink-3 whitespace-nowrap">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <Badge tone={user.isAdmin ? 'accent' : 'neutral'}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-[13px] whitespace-nowrap">
                    <button
                      onClick={() => handleToggleAdmin(user.id)}
                      className={`p-1.5 rounded transition-colors text-ink-3 ${
                        user.isAdmin ? 'hover:text-danger' : 'hover:text-accent'
                      }`}
                      title={user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                    >
                      <Shield className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
